import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import "./_linked-items-picker.scss";

/**
 * Searchable multi-select checklist with an optional "+ Add new X" link -
 * replaces the old-package FormInputListManager for linking catalog items
 * (system features, add-on products, etc.) into a plan/product form.
 * `selected`/`onChange` report full item objects, matching how the rest of
 * this codebase's form Controllers work.
 *
 * The trigger is always a static button (never swaps for a search field) -
 * clicking it opens a panel with the search box as its own row above the
 * option list, mirroring Select/CompanySelect's "search lives inside the
 * opened panel" pattern. The panel floats over whatever's below it
 * (CSS `position: absolute` off `&__control`, no portal - this never lives
 * in a scrollable/clipping ancestor the way Select can) rather than
 * pushing the page down, so it reads as a transient dropdown like every
 * other one in the app instead of a block fused into the page layout.
 * Stays open while checking/unchecking items (this is a multi-select, so
 * unlike Select it doesn't close on an option click) and closes on
 * outside click or Escape.
 */
const LinkedItemsPicker = ({ label, disclaimer, items = [], selected = [], valueKey = "_id", labelKey, onChange, ctaLabel, ctaHref, isLoading, emptyMessage = "No items found" }) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  // Off-screen row holding EVERY selected label (not just the visible
  // subset) purely so its chips can be measured at their natural width -
  // the visible trigger only ever renders the computed visibleCount, so
  // measuring the visible row alone could never detect "more would now
  // fit" after the trigger grows wider again.
  const measureRowRef = useRef(null);
  // How many selected chips fit on one line before the rest collapse into
  // a trailing "+N" chip - starts optimistic (show everything) so the very
  // first paint never shows a "0 selected"/empty flash; the layout effect
  // below corrects it synchronously before the browser paints. Same
  // technique and math as Select's own chip overflow (real bordered chip
  // pills, not comma-joined text, so "+N" renders as its own chip right
  // after the last visible one instead of tacked onto a text blob).
  const [visibleCount, setVisibleCount] = useState(selected.length);

  // useLayoutEffect (not useEffect) so the corrected count commits before
  // paint - the trigger's real content width vs. each chip's natural width
  // (from the hidden measure row) decides how many fit, reserving room for
  // the "+N" chip, and always keeping at least the first chip visible
  // rather than showing nothing.
  //
  // Deliberately no dependency array (runs every render, not just when
  // isOpen/selected.length change) - the ResizeObserver below re-triggers
  // measurement purely by resetting visibleCount to selected.length, an
  // unchanged value when the list itself didn't change, so a deps array
  // here would silently skip re-measuring after a resize. Doesn't skip
  // while `isOpen` either - the trigger's content stays the same whether
  // the panel is open or not (mirrors Select's own chip measurement).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const measureRow = measureRowRef.current;
    if (!trigger || !measureRow || selected.length === 0) return;

    const containerWidth = trigger.clientWidth;
    const chipGap = 4; // matches .linked-items-picker__chips { gap: 4px }
    const chipNodes = Array.from(measureRow.querySelectorAll("[data-chip]"));
    const suffixNode = measureRow.querySelector("[data-suffix]");
    const suffixWidth = suffixNode ? suffixNode.offsetWidth : 0;

    let used = 0;
    let count = 0;
    for (let i = 0; i < chipNodes.length; i++) {
      const width = chipNodes[i].offsetWidth + (i === 0 ? 0 : chipGap);
      const hasMoreAfter = i < chipNodes.length - 1;
      const reserve = hasMoreAfter ? suffixWidth + chipGap : 0;
      if (i === 0 || used + width + reserve <= containerWidth) {
        used += width;
        count = i + 1;
      } else {
        break;
      }
    }
    setVisibleCount(count);
  });

  // Re-measure when the trigger's own width changes (sidebar resize,
  // viewport resize) - resetting to the full count re-runs the layout
  // effect above against the new width on the next render.
  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setVisibleCount(selected.length));
    observer.observe(trigger);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [isOpen]);

  // Autofocus the search box every time the panel opens, mirroring
  // Select/CompanySelect - typing to filter works immediately.
  useEffect(() => {
    if (isOpen) searchInputRef.current?.focus();
  }, [isOpen]);

  const filtered = items.filter((item) => (item?.[labelKey] || "").toLowerCase().includes(search.toLowerCase()));
  const isSelected = (item) => selected.some((s) => s?.[valueKey] === item?.[valueKey]);

  const toggle = (item) => {
    if (isSelected(item)) {
      onChange(selected.filter((s) => s?.[valueKey] !== item?.[valueKey]));
    } else {
      onChange([...selected, item]);
    }
  };

  const openPicker = () => {
    setSearch("");
    setIsOpen(true);
  };

  const hiddenCount = selected.length - visibleCount;

  return (
    <div className="linked-items-picker" ref={containerRef}>
      {label && <span className="linked-items-picker__label">{label}</span>}
      {disclaimer && <p className="linked-items-picker__disclaimer">{disclaimer}</p>}

      <div className="linked-items-picker__control">
        {/* role="button" div, not a native <button> - it hosts each chip's
            own remove <button> below, and a button can't contain a button
            (invalid HTML, breaks click handling) - same reasoning as
            Select's own trigger. */}
        <div
          role="button"
          tabIndex={0}
          className="linked-items-picker__trigger"
          onClick={() => (isOpen ? setIsOpen(false) : openPicker())}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              isOpen ? setIsOpen(false) : openPicker();
            }
          }}
          ref={triggerRef}
        >
          {selected.length === 0 ? (
            <span className="linked-items-picker__trigger-placeholder">Select</span>
          ) : (
            <span className="linked-items-picker__chips">
              {selected.slice(0, visibleCount).map((item) => (
                <span className="linked-items-picker__chip" key={item[valueKey]}>
                  <span className="linked-items-picker__chip-label">{item[labelKey]}</span>
                  <button
                    type="button"
                    className="linked-items-picker__chip-remove"
                    aria-label={`Remove ${item[labelKey]}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggle(item);
                    }}
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
              {hiddenCount > 0 && (
                <span className="linked-items-picker__chip linked-items-picker__chip--overflow">
                  +{hiddenCount}
                </span>
              )}
              {/* Off-screen, holds every chip (not just the visible slice)
                  at natural width purely so the layout effect above can
                  measure them - identical technique to Select's own
                  &__measure-row. */}
              <span ref={measureRowRef} className="linked-items-picker__measure-row" aria-hidden="true">
                {selected.map((item) => (
                  <span className="linked-items-picker__chip" key={item[valueKey]} data-chip>
                    <span className="linked-items-picker__chip-label">{item[labelKey]}</span>
                    <span className="linked-items-picker__chip-remove">
                      <X size={10} />
                    </span>
                  </span>
                ))}
                <span className="linked-items-picker__chip linked-items-picker__chip--overflow" data-suffix>
                  +{selected.length}
                </span>
              </span>
            </span>
          )}
        </div>

        {isOpen && (
          <div className="linked-items-picker__panel">
            <div className="linked-items-picker__search-row">
              <input
                ref={searchInputRef}
                type="text"
                className="linked-items-picker__search-input"
                placeholder="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => event.key === "Escape" && setIsOpen(false)}
              />
            </div>
            <ul className="linked-items-picker__list">
              {isLoading ? (
                <li className="linked-items-picker__empty">Loading...</li>
              ) : filtered.length === 0 ? (
                <li className="linked-items-picker__empty">{emptyMessage}</li>
              ) : (
                filtered.map((item) => (
                  <li key={item[valueKey]}>
                    <button
                      type="button"
                      className={`linked-items-picker__option ${isSelected(item) ? "linked-items-picker__option--selected" : ""}`}
                      onClick={() => toggle(item)}
                    >
                      <span className="linked-items-picker__option-label">{item[labelKey]}</span>
                      {isSelected(item) && <Check size={14} className="linked-items-picker__check" />}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>

      {ctaHref && (
        <Link className="linked-items-picker__cta" to={ctaHref}>
          {ctaLabel}
        </Link>
      )}
    </div>
  );
};

export default LinkedItemsPicker;
